// src/pages/VerifyEmail.js – NextScenes Verify Email
// Build: 2026-01-10-verify-email-page-v3 (hardened token parsing + safer errors + retry)

import React, { useEffect, useMemo, useState } from "react";
import "../App.css";
import { api } from "../api";

function extractToken() {
  try {
    // 1) Standard: /verify-email?token=...
    const qs = new URLSearchParams(window.location.search || "");
    const t1 = String(qs.get("token") || "").trim();
    if (t1) return t1;

    // 2) Some email clients rewrite links into fragments:
    // /verify-email#token=...
    // /verify-email#/verify-email?token=...
    const hash = String(window.location.hash || "");
    if (!hash) return "";

    // Try to find token=... inside hash
    const idx = hash.toLowerCase().indexOf("token=");
    if (idx >= 0) {
      const tail = hash.slice(idx);
      const qs2 = new URLSearchParams(tail.replace(/^#/, "").replace(/^\//, ""));
      const t2 = String(qs2.get("token") || "").trim();
      if (t2) return t2;

      // If hash is like "#token=xxxxx&..."
      const m = tail.match(/token=([^&]+)/i);
      if (m && m[1]) return decodeURIComponent(m[1]);
    }

    return "";
  } catch {
    return "";
  }
}

function pickErrorMessage(ex) {
  // Your api() wrapper usually throws Error(message), not axios-style objects.
  const raw = ex?.message ? String(ex.message) : "";

  // If api() throws something richer, try to grab it too.
  const dataMsg =
    ex?.data?.message ||
    ex?.response?.data?.message ||
    ex?.payload?.message ||
    ex?.body?.message ||
    "";

  const best = String(dataMsg || raw || "").trim();

  if (!best) return "Verification failed. The link may be invalid or expired.";

  // Make a slightly friendlier output for common cases
  if (best.toLowerCase().includes("invalid") || best.toLowerCase().includes("expired")) {
    return "This verification link is invalid or has expired. Please request a new verification email.";
  }

  return best;
}

export default function VerifyEmail({ onNavigate, onNavigatePage }) {
  const nav =
    typeof onNavigate === "function"
      ? onNavigate
      : typeof onNavigatePage === "function"
        ? onNavigatePage
        : null;

  const token = useMemo(() => extractToken(), []);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [attempt, setAttempt] = useState(1);

  useEffect(() => {
    let alive = true;

    async function run() {
      if (!token) {
        if (!alive) return;
        setBusy(false);
        setOk(false);
        setMsg("");
        setErr("Missing verification token. Please use the verification link from your email.");
        return;
      }

      if (!alive) return;
      setBusy(true);
      setErr("");
      setMsg("");
      setOk(false);

      try {
        const res = await api("/api/auth/verify-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });

        if (!alive) return;

        setOk(true);
        setMsg(String(res?.message || "Email verified. You can log in now."));
      } catch (ex) {
        if (!alive) return;
        setOk(false);
        setErr(pickErrorMessage(ex));
      } finally {
        if (!alive) return;
        setBusy(false);
      }
    }

    run();

    return () => {
      alive = false;
    };
  }, [token, attempt]);

  return (
    <div className="page">
      <div className="card" style={{ maxWidth: 520, margin: "0 auto" }}>
        <h2 style={{ marginTop: 0 }}>Verify Email</h2>

        {busy ? (
          <div className="banner" style={{ marginTop: 12 }}>
            Verifying…
          </div>
        ) : null}

        {err ? (
          <div className="banner" style={{ marginTop: 12 }}>
            {err}
          </div>
        ) : null}

        {msg ? (
          <div className="banner" style={{ marginTop: 12, background: "#dcfce7" }}>
            {msg}
          </div>
        ) : null}

        <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="btn" type="button" onClick={() => nav && nav("login")} disabled={busy || !ok}>
            Go to Login
          </button>

          <button
            className="btn"
            type="button"
            onClick={() => setAttempt((n) => n + 1)}
            disabled={busy}
            title="Try verification again"
          >
            Retry
          </button>

          <button
            className="linkBtn"
            type="button"
            onClick={() => nav && nav("home")}
            disabled={busy}
            style={{ fontWeight: 800 }}
          >
            Go Home
          </button>
        </div>

        {!token ? (
          <div style={{ marginTop: 14, fontSize: 12, color: "#64748b" }}>
            Tip: open the verification email on a desktop browser if your phone mail app breaks the link.
          </div>
        ) : null}
      </div>
    </div>
  );
}
